import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import {
  FiX,
  FiPackage,
  FiCheck,
  FiArrowRight,
  FiArrowLeft,
  FiShield,
  FiUpload,
  FiFile,
  FiInfo,
  FiCopy,
  FiDownload,
  FiCpu,
  FiRefreshCw,
  FiEye
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useAssetClass } from '../../contexts/AssetClassContext';
import { useTokenization } from '../../contexts/TokenizationContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import TransactionLookupDrawer from '../BlockchainExplorer/TransactionLookupDrawer';
import {
  CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID,
  DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID,
  getDeclaredTokenStandardForContractTemplate,
  listStandardContractTemplates,
  resolveContractArtifact
} from '../../blockchain/standardContractTemplates';
import {
  normalizeTokenStandardKey,
  resolveContractTemplateIdForAssetClass
} from '../../blockchain/resolveMintArtifactForAssetClass';
import { hasAssetClassDeployedContract } from '../../utils/assetClassContractUiAccess';
import {
  TOKENIZABLE_ASSET_STATUSES,
  buildSignerOutputTemplate,
  copyTextToClipboard,
  delay,
  downloadJsonFile,
  formatPreparedPayload,
  incrementNonceHex,
  parseSignedTransactionInput,
  sanitizeContractName
} from '../../blockchain/offlineSigning';
import { buildSharedSeries721ConstructorParameters } from '../../blockchain/deployConstructorParams';
import toast from 'react-hot-toast';
import {
  getOfflineSignerAddressForUser,
  setOfflineSignerAddressForUser
} from '../../api/offlineSignerPreferences';
import { ipfsApi } from '../../api/smartTokenizerApi';
import {
  assetDocumentRowKey,
  buildAssetDocumentPreviewBlob,
  openAssetDocumentObjectUrl
} from '../../utils/assetDocumentPreview';

const WizardContainer = styled.div`
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const WizardHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const WizardTitle = styled.h2`
  color: #fff;
  font-size: 1.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    color: #4facfe;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #a0a0a0;
  font-size: 1.5rem;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: #fff;
  }
`;

const ProgressBar = styled.div`
  margin-bottom: 2rem;
`;

const ProgressSteps = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const ProgressStep = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  
  &:not(:last-child)::after {
    content: '';
    flex: 1;
    height: 2px;
    background: ${props => props.completed ? '#4facfe' : 'rgba(255, 255, 255, 0.1)'};
    margin: 0 1rem;
  }

  .step-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: ${props => {
      if (props.completed) return '#4facfe';
      if (props.active) return 'rgba(79, 172, 254, 0.2)';
      return 'rgba(255, 255, 255, 0.1)';
    }};
    border: 2px solid ${props => {
      if (props.completed || props.active) return '#4facfe';
      return 'rgba(255, 255, 255, 0.2)';
    }};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.completed || props.active ? '#4facfe' : '#a0a0a0'};
    font-weight: 600;
  }

  .step-label {
    margin-left: 0.75rem;
    color: ${props => props.completed || props.active ? '#4facfe' : '#a0a0a0'};
    font-size: 0.9rem;
    font-weight: 500;
  }
`;

const StepContent = styled(motion.div)`
  min-height: 400px;
  margin-bottom: 2rem;
`;

const AssetClassInfo = styled.div`
  background: rgba(79, 172, 254, 0.05);
  border: 1px solid rgba(79, 172, 254, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;

  .class-name {
    font-size: 1.3rem;
    font-weight: 600;
    color: #4facfe;
    margin-bottom: 0.5rem;
  }

  .class-details {
    color: #a0a0a0;
    margin-bottom: 1rem;
  }

  .class-schema {
    .schema-title {
      font-weight: 600;
      color: #fff;
      margin-bottom: 0.75rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .required-fields {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .field-tag {
      background: rgba(79, 172, 254, 0.1);
      border: 1px solid rgba(79, 172, 254, 0.3);
      border-radius: 6px;
      padding: 0.5rem 0.75rem;
      color: #4facfe;
      font-size: 0.85rem;
      font-weight: 500;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;

  .section-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
      color: #4facfe;
    }
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr'};
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;

  label {
    color: #fff;
    font-weight: 500;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }

  input, select, textarea {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 0.75rem;
    color: #fff;
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &:focus {
      border-color: #4facfe;
      box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
    }

    &::placeholder {
      color: #a0a0a0;
    }
  }

  select {
    option {
      background: #1a1a3e;
      color: #fff;
    }
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }

  .field-hint {
    font-size: 0.8rem;
    color: #a0a0a0;
    margin-top: 0.25rem;
  }

  .field-error {
    font-size: 0.8rem;
    color: #ff6b6b;
    margin-top: 0.25rem;
  }
`;

const DocumentUpload = styled.div`
  border: 2px dashed rgba(79, 172, 254, 0.3);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #4facfe;
    background: rgba(79, 172, 254, 0.05);
  }

  .upload-icon {
    font-size: 3rem;
    color: #4facfe;
    margin-bottom: 1rem;
  }

  .upload-text {
    color: #fff;
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  .upload-hint {
    color: #a0a0a0;
    font-size: 0.9rem;
  }
`;

const UploadedFiles = styled.div`
  margin-top: 1rem;

  .file-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    background: rgba(79, 172, 254, 0.1);
    border: 1px solid rgba(79, 172, 254, 0.2);
    border-radius: 8px;
    margin-bottom: 0.5rem;

    .file-icon {
      color: #4facfe;
    }

    .file-name {
      color: #fff;
      font-weight: 500;
      flex: 1;
    }

    .file-size {
      color: #a0a0a0;
      font-size: 0.85rem;
      white-space: nowrap;
    }

    .file-open {
      flex-shrink: 0;
      margin-left: auto;
    }
  }
`;

const ReviewSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;

  .review-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
      color: #4facfe;
    }
  }

  .review-item {
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    &:last-child {
      border-bottom: none;
    }

    .item-label {
      color: #a0a0a0;
      font-weight: 500;
    }

    .item-value {
      color: #fff;
      font-weight: 600;
    }
  }
`;

const StatusPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;

  .title {
    color: #4facfe;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .message {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 0.75rem;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    color: #a0a0a0;
    font-size: 0.9rem;
  }
`;

const PayloadBox = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  .title {
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .hint {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 1rem;
    font-size: 0.9rem;
  }

  textarea {
    width: 100%;
    min-height: 160px;
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 1rem;
    resize: vertical;
    font-family: Consolas, Monaco, monospace;
    font-size: 0.85rem;
  }
`;

const PayloadActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const MiniButton = styled.button.attrs({ type: 'button' })`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const FilePickLabel = styled.label`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  input {
    display: none;
  }
`;

const FlowSteps = styled.ol`
  margin: 0 0 1rem;
  padding-left: 1.25rem;
  color: #a0a0a0;
  line-height: 1.55;
  font-size: 0.9rem;

  li {
    margin-bottom: 0.5rem;
  }

  strong {
    color: #e2e8f0;
    font-weight: 600;
  }

  code {
    font-size: 0.85em;
    color: #9bd6ff;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${({ status }) =>
    status === 'confirmed' || status === 'success' || status === 'minted'
      ? 'rgba(0, 255, 136, 0.12)'
      : status === 'failed'
        ? 'rgba(255, 107, 107, 0.12)'
        : 'rgba(79, 172, 254, 0.12)'};
  color: ${({ status }) =>
    status === 'confirmed' || status === 'success' || status === 'minted'
      ? '#00ff88'
      : status === 'failed'
        ? '#ff6b6b'
        : '#4facfe'};
  border: 1px solid currentColor;
`;

const HashButton = styled.button.attrs({ type: 'button' })`
  background: transparent;
  border: none;
  padding: 0;
  color: #4facfe;
  font-family: Consolas, Monaco, monospace;
  cursor: pointer;
  text-decoration: underline;
`;

/** Matches deploy-contract modal offline actions */
const OfflineFlowActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
  align-items: center;
`;

const OfflineFlowPrimaryButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  background: #4facfe;
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #3d8bfe;
  }

  &:disabled {
    background: #2a2a3e;
    color: #a0a0a0;
    cursor: not-allowed;
  }
`;

const OfflineFlowSecondaryButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.14);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const WizardActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #4facfe, #00f2fe);
    color: #fff;
    border: none;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(79, 172, 254, 0.3);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  `}
`;

const LoadingOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100;

  .loading-text {
    color: #fff;
    font-size: 1.1rem;
    margin-top: 1rem;
  }

  .loading-spinner {
    width: 50px;
    height: 50px;
    border: 3px solid rgba(79, 172, 254, 0.3);
    border-top: 3px solid #4facfe;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const steps = [
  { id: 1, label: 'Batch Details' },
  { id: 2, label: 'Token Configuration' },
  { id: 3, label: 'Metadata' },
  { id: 4, label: 'Documents' },
  { id: 5, label: 'Review & Mint' }
];

/** Progress bar when tokenizing a single existing asset from Assets Registry (skip batch step 1). */
const REGISTRY_TOKENIZE_BAR_STEPS = [
  { id: 2, label: 'Token Configuration' },
  { id: 3, label: 'Metadata' },
  { id: 4, label: 'Documents' },
  { id: 5, label: 'Review & Mint' }
];

function BatchMintingWizard({ assetClass, onClose, preselectedAsset = null }) {
  const {
    addBatchToAssetClass,
    validateMetadataSchema,
    loading,
    getAssetClassById,
    prepareContractDeployment,
    submitContractDeployment
  } = useAssetClass();
  const {
    assets,
    getAssetById,
    prepareMintToken,
    allocateErc721TokenIds,
    allocateErc1155TokenIds,
    submitMintToken,
    getMintedTokenByAssetId
  } = useTokenization();
  const { currentIssuer, session, isAuthorizedTokenizer, isAdmin } = useTokenIssuer();

  const isRegistryTokenizeFlow = Boolean(
    preselectedAsset?.id && assetClass?.id && preselectedAsset.assetClassId === assetClass.id
  );
  const minStep = isRegistryTokenizeFlow ? 2 : 1;
  const stepsForProgressBar = isRegistryTokenizeFlow ? REGISTRY_TOKENIZE_BAR_STEPS : steps;

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    quantity: '',
    unitPrice: '',
    description: '',
    metadata: {},
    documents: [],
    // Token minting specific fields
    ownerAddress: '',
    custodianAddress: '',
    tokenSymbolPrefix: '',
    startingSerialNumber: '',
    enableAutoSerialization: true,
    mintingLocation: '',
    qualityCertificationId: '',
    insurancePolicyId: '',
    storageConditions: '',
    expirationDate: '',
    mintingReason: 'NEW_INVENTORY',
    signerAddress: '',
    erc20MintAmount: '1000000000000000000',
    erc1155MintAmount: '1'
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preparedDeployment, setPreparedDeployment] = useState(null);
  const [deploymentStatus, setDeploymentStatus] = useState(null);
  const [signedDeploymentInput, setSignedDeploymentInput] = useState('');
  const [preparedMintBatch, setPreparedMintBatch] = useState([]);
  const [mintStatus, setMintStatus] = useState(null);
  const [signedMintBatchInput, setSignedMintBatchInput] = useState('');
  /** Set after local deploy poll succeeds so mint UI works before parent refreshes assetClass. */
  const [resolvedContractAddress, setResolvedContractAddress] = useState(null);
  const [createdBatch, setCreatedBatch] = useState(null);
  const [inspectedTransactionHash, setInspectedTransactionHash] = useState(null);
  const [contractTemplateId, setContractTemplateId] = useState(DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID);
  const [customContractAbiJson, setCustomContractAbiJson] = useState('');
  const [customContractBytecode, setCustomContractBytecode] = useState('');
  const [customMintFunctionName, setCustomMintFunctionName] = useState('mint');
  const [openingDocumentKey, setOpeningDocumentKey] = useState(null);

  const currentUserId = currentIssuer?.userId || session?.userId || currentIssuer?.id || null;
  const classHasDeployedContract = hasAssetClassDeployedContract(assetClass);
  const mintContractTemplateId = classHasDeployedContract
    ? resolveContractTemplateIdForAssetClass(assetClass)
    : contractTemplateId;
  const eligibleAssets = assets.filter(
    (asset) => asset.assetClassId === assetClass?.id && TOKENIZABLE_ASSET_STATUSES.has(asset.status) && !asset.tokenizationId
  );
  const singleSignerTemplate = formatPreparedPayload(buildSignerOutputTemplate());
  const batchSignerTemplate = formatPreparedPayload([
    buildSignerOutputTemplate({ transactionHash: '0xFirstSignedTxHash', summary: 'Batch token minting' }),
    buildSignerOutputTemplate({ transactionHash: '0xSecondSignedTxHash', summary: 'Batch token minting' })
  ]);
  const mintSingleSignerTemplate = formatPreparedPayload(
    buildSignerOutputTemplate({ summary: 'Token minting' })
  );
  const mintSignerTemplateForDisplay =
    preparedMintBatch.length === 1 ? mintSingleSignerTemplate : batchSignerTemplate;

  const mintPreparedUnifiedPayload =
    preparedMintBatch.length === 1
      ? preparedMintBatch[0]?.payload
      : preparedMintBatch.map((row) => row.payload);

  const mintPayloadAnchorRef = useRef(null);
  useEffect(() => {
    if (preparedMintBatch.length > 0) {
      mintPayloadAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [preparedMintBatch.length]);

  // Reset form when modal opens or assetClass / registry asset changes
  useEffect(() => {
    if (!assetClass) return;

    const registryFlow =
      Boolean(preselectedAsset?.id) && preselectedAsset.assetClassId === assetClass.id;

    setValidationErrors({});
    setIsSubmitting(false);
    setPreparedDeployment(null);
    setDeploymentStatus(null);
    setSignedDeploymentInput('');
    setPreparedMintBatch([]);
    setMintStatus(null);
    setSignedMintBatchInput('');
    setResolvedContractAddress(String(assetClass.contractAddress || '').trim() || null);
    setCreatedBatch(null);
    setContractTemplateId((prev) => {
      const deployed = Boolean(String(assetClass?.contractAddress || '').trim());
      if (deployed) {
        return resolveContractTemplateIdForAssetClass(assetClass);
      }
      if (prev === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID) {
        return prev;
      }
      return resolveContractTemplateIdForAssetClass(assetClass);
    });
    setCustomContractAbiJson('');
    setCustomContractBytecode('');
    setCustomMintFunctionName('mint');

    if (registryFlow) {
      setCurrentStep(2);
      const initialMetadata = {};
      if (assetClass.standardSchema?.required) {
        assetClass.standardSchema.required.forEach((field) => {
          initialMetadata[field] = '';
        });
      }
      const srcMeta = preselectedAsset.metadata && typeof preselectedAsset.metadata === 'object'
        ? { ...preselectedAsset.metadata }
        : {};
      Object.keys(initialMetadata).forEach((k) => {
        if (srcMeta[k] !== undefined && srcMeta[k] !== null) {
          initialMetadata[k] = srcMeta[k];
        }
      });
      Object.keys(srcMeta).forEach((k) => {
        if (!(k in initialMetadata)) initialMetadata[k] = srcMeta[k];
      });

      const docs = (preselectedAsset.documents || []).map((d, i) => ({
        ...d,
        id: String(d.id ?? d.documentId ?? d.DocumentId ?? `doc-${i}`),
        name: String(d.fileName ?? d.FileName ?? d.name ?? 'Document'),
        size: Number(d.sizeBytes ?? d.fileSize ?? d.Size ?? 0) || 0,
        type: String(d.contentType ?? d.ContentType ?? 'application/octet-stream')
      }));

      const sym = String(assetClass.marketSymbol || assetClass.symbol || '').trim();

      setFormData({
        quantity: '1',
        unitPrice: String(Number(preselectedAsset.valuationUSD) || 0),
        description: String(
          preselectedAsset.description || preselectedAsset.name || preselectedAsset.title || 'Tokenization'
        ).slice(0, 500),
        metadata: initialMetadata,
        documents: docs,
        ownerAddress: String(preselectedAsset.ownerAddress || '').trim(),
        custodianAddress: String(preselectedAsset.custodian || '').trim(),
        tokenSymbolPrefix: sym ? `${sym}-` : '',
        startingSerialNumber: '',
        enableAutoSerialization: true,
        mintingLocation:
          String(preselectedAsset.location || preselectedAsset.metadata?.location || '').trim() || '—',
        qualityCertificationId: '',
        insurancePolicyId: '',
        storageConditions: '',
        expirationDate: '',
        mintingReason: 'NEW_INVENTORY',
        erc20MintAmount: '1000000000000000000',
        erc1155MintAmount: '1',
        signerAddress: (() => {
          const tw = String(currentIssuer?.walletAddress || '').trim();
          if (isAuthorizedTokenizer && tw) return tw;
          return getOfflineSignerAddressForUser(currentUserId) || tw || '';
        })()
      });
      return;
    }

    setCurrentStep(1);
    const initialMetadata = {};
    if (assetClass.standardSchema?.required) {
      assetClass.standardSchema.required.forEach((field) => {
        initialMetadata[field] = '';
      });
    }

    setFormData({
      quantity: '',
      unitPrice: '',
      description: '',
      metadata: initialMetadata,
      documents: [],
      ownerAddress: '',
      custodianAddress: '',
      tokenSymbolPrefix: assetClass.symbol || '',
      startingSerialNumber: '',
      enableAutoSerialization: true,
      mintingLocation: '',
      qualityCertificationId: '',
      insurancePolicyId: '',
      storageConditions: '',
      expirationDate: '',
      mintingReason: 'NEW_INVENTORY',
      erc20MintAmount: '1000000000000000000',
      erc1155MintAmount: '1',
      signerAddress: (() => {
        const tw = String(currentIssuer?.walletAddress || '').trim();
        if (isAuthorizedTokenizer && tw) return tw;
        return getOfflineSignerAddressForUser(currentUserId) || tw || '';
      })()
    });
    // Intentionally depend on class + registry asset identity, not every nested field reference, to avoid
    // re-seeding the whole form when parent passes new object identities for the same asset.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [
    assetClass?.id,
    assetClass?.category,
    assetClass?.contractAddress,
    assetClass?.marketSymbol,
    assetClass?.symbol,
    preselectedAsset?.id,
    preselectedAsset?.assetClassId,
    currentUserId,
    isAuthorizedTokenizer,
    currentIssuer?.walletAddress
  ]);

  useEffect(() => {
    if (!assetClass?.id) return;
    const tw = String(currentIssuer?.walletAddress || '').trim();
    if (isAuthorizedTokenizer) {
      if (!tw || !currentUserId) return;
      setFormData((prev) => {
        if (prev.signerAddress === tw) return prev;
        setOfflineSignerAddressForUser(currentUserId, tw);
        return { ...prev, signerAddress: tw };
      });
      return;
    }
    if (getOfflineSignerAddressForUser(currentUserId)) return;
    if (!tw) return;
    setFormData((prev) => {
      if (String(prev.signerAddress || '').trim()) return prev;
      return { ...prev, signerAddress: tw };
    });
  }, [assetClass?.id, currentUserId, currentIssuer?.walletAddress, isAuthorizedTokenizer]);

  const updateFormData = (field, value) => {
    if (field === 'signerAddress') {
      setOfflineSignerAddressForUser(currentUserId, value);
    }
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const updateMetadata = (field, value) => {
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [field]: value
      }
    }));
  };

  const handleFileUpload = (files) => {
    const newFiles = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      nativeFile: file
    }));
    
    setFormData(prev => ({
      ...prev,
      documents: [...prev.documents, ...newFiles]
    }));
  };

  const openWizardDocument = useCallback(async (file) => {
    const rowKey = assetDocumentRowKey(file);
    const native = file?.nativeFile;

    if (native instanceof File) {
      setOpeningDocumentKey(rowKey);
      try {
        const url = URL.createObjectURL(native);
        const docForName = { ...file, fileName: file.name, FileName: file.name };
        const opened = openAssetDocumentObjectUrl(
          url,
          native.type || file.type || 'application/octet-stream',
          docForName
        );
        if (!opened) {
          toast.error('Pop-up blocked. Allow pop-ups to view the document.');
          URL.revokeObjectURL(url);
          return;
        }
        window.setTimeout(() => URL.revokeObjectURL(url), 600000);
      } catch (e) {
        toast.error(e.response?.data?.message || e.message || 'Could not open document');
      } finally {
        setOpeningDocumentKey(null);
      }
      return;
    }

    const cid = String(file?.ipfsCid ?? file?.IpfsCid ?? '').trim();
    if (!cid) {
      toast.error('No IPFS CID for this document.');
      return;
    }

    setOpeningDocumentKey(rowKey);
    try {
      const response = await ipfsApi.downloadByCid(cid);
      const blob = await buildAssetDocumentPreviewBlob(response, file);
      const url = URL.createObjectURL(blob);
      const opened = openAssetDocumentObjectUrl(url, blob.type || 'application/octet-stream', file);
      if (!opened) {
        toast.error('Pop-up blocked. Allow pop-ups to view the document.');
        URL.revokeObjectURL(url);
        return;
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 600000);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Could not load document');
    } finally {
      setOpeningDocumentKey(null);
    }
  }, []);

  const validateCurrentStep = () => {
    const errors = {};
    
    switch (currentStep) {
      case 1:
        if (!formData.quantity) errors.quantity = 'Quantity is required';
        if (!formData.unitPrice) errors.unitPrice = 'Unit price is required';
        if (!formData.description) errors.description = 'Description is required';
        if (parseInt(formData.quantity || 0, 10) > eligibleAssets.length) {
          errors.quantity = `Only ${eligibleAssets.length} approved assets in this class are ready for minting.`;
        }
        if (!classHasDeployedContract && contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID) {
          try {
            resolveContractArtifact(CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID, {
              abiJson: customContractAbiJson,
              bytecode: customContractBytecode,
              mintFunctionName: customMintFunctionName
            });
          } catch (e) {
            errors.customContract = e.message;
          }
          if (!String(customMintFunctionName || '').trim()) {
            errors.customMintFn = 'Mint function name is required.';
          }
          if (!assetClass?.contractAddress) {
            const bc = String(customContractBytecode || '').trim();
            if (!bc.startsWith('0x') || bc.length < 4) {
              errors.customBytecode = 'Creation bytecode (0x…) is required to deploy your custom contract.';
            }
          }
        }
        break;
      
      case 2:
        if (!formData.ownerAddress) errors.ownerAddress = 'Owner address is required';
        if (!formData.custodianAddress) errors.custodianAddress = 'Custodian address is required';
        if (!formData.signerAddress) errors.signerAddress = 'Offline signer address is required';
        if (!formData.tokenSymbolPrefix) errors.tokenSymbolPrefix = 'Token symbol prefix is required';
        if (!formData.mintingLocation) errors.mintingLocation = 'Minting location is required';
        break;
      
      case 3:
        if (!isRegistryTokenizeFlow) {
          const { messages: schemaMessages } = validateMetadataSchema(
            formData.metadata,
            assetClass.standardSchema
          );
          if (schemaMessages.length > 0) {
            errors.metadata = schemaMessages.join(', ');
          }
        }
        break;

      case 4:
        if (!isRegistryTokenizeFlow && formData.documents.length === 0) {
          errors.documents = 'At least one document is required';
        }
        break;
        
      default:
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, minStep));
  };

  const getSelectedAssetsForBatch = () => {
    if (isRegistryTokenizeFlow && preselectedAsset?.id) {
      const row = assets.find((a) => a.id === preselectedAsset.id);
      if (row) return [row];
      return [
        {
          id: preselectedAsset.id,
          name: preselectedAsset.name || preselectedAsset.title,
          title: preselectedAsset.title,
          assetClassId: preselectedAsset.assetClassId,
          status: preselectedAsset.status,
          createdAt: preselectedAsset.createdAt
        }
      ];
    }
    return eligibleAssets
      .slice()
      .sort(
        (left, right) =>
          new Date(left.createdAt || 0).getTime() - new Date(right.createdAt || 0).getTime()
      )
      .slice(0, parseInt(formData.quantity || 0, 10));
  };

  /** Cap used in review / offline-flow labels: registry uses the selected asset only, not the full eligible pool. */
  const targetMintPoolSize = isRegistryTokenizeFlow
    ? getSelectedAssetsForBatch().length
    : eligibleAssets.length;

  const pollForDeploymentConfirmation = async (assetClassId) => {
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const refreshedAssetClass = await getAssetClassById(assetClassId, true);
      if (refreshedAssetClass?.contractAddress) {
        return refreshedAssetClass;
      }

      await delay(5000);
    }

    return getAssetClassById(assetClassId, true);
  };

  const pollForMintedAsset = async (assetId) => {
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const mintedToken = await getMintedTokenByAssetId(assetId);
      if (mintedToken) {
        return mintedToken;
      }

      const asset = await getAssetById(assetId, true);
      if (asset?.tokenizationId) {
        return getMintedTokenByAssetId(assetId);
      }

      await delay(5000);
    }

    return null;
  };

  const handleCopyPayload = async (payload) => {
    await copyTextToClipboard(formatPreparedPayload(payload));
    toast.success('Copied JSON payload');
  };

  const handleCopyTemplate = async (template) => {
    await copyTextToClipboard(template);
    toast.success('Copied signer template');
  };

  const handleDownloadPayload = (filename, payload) => {
    downloadJsonFile(filename, payload);
    toast.success(`Downloaded ${filename}`);
  };

  const handleSignedDeploymentFileSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '').trim();
      if (!text) {
        toast.error('File was empty');
        return;
      }
      setSignedDeploymentInput(text);
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => toast.error('Could not read file');
    reader.readAsText(file, 'utf-8');
    event.target.value = '';
  };

  const handleSignedBatchMintFileSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '').trim();
      if (!text) {
        toast.error('File was empty');
        return;
      }
      setSignedMintBatchInput(text);
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => toast.error('Could not read file');
    reader.readAsText(file, 'utf-8');
    event.target.value = '';
  };

  const contractAddressEffective = () =>
    String(resolvedContractAddress || assetClass?.contractAddress || '').trim();

  const resetDeploymentOffline = () => {
    setPreparedDeployment(null);
    setSignedDeploymentInput('');
  };

  const resetMintOffline = () => {
    setPreparedMintBatch([]);
    setSignedMintBatchInput('');
  };

  const ensureBatchRecord = async () => {
    if (createdBatch) return createdBatch;
    const batchData = {
      quantity: parseInt(formData.quantity, 10),
      unitPrice: parseFloat(formData.unitPrice),
      description: formData.description,
      metadata: formData.metadata,
      documents: formData.documents
    };
    const batch = await addBatchToAssetClass(assetClass.id, batchData);
    setCreatedBatch(batch);
    return batch;
  };

  const validateStep5ForOffline = () => {
    if (!validateCurrentStep()) return null;
    let contractArtifact;
    try {
      contractArtifact = resolveContractArtifact(mintContractTemplateId, {
        abiJson: customContractAbiJson,
        bytecode: customContractBytecode,
        mintFunctionName: customMintFunctionName
      });
    } catch (e) {
      toast.error(e.message || 'Invalid contract template');
      return null;
    }
    if (!currentUserId) {
      toast.error('Please sign in before preparing transactions.');
      return null;
    }
    if (!String(formData.signerAddress || '').trim()) {
      toast.error('Offline signer address is required.');
      return null;
    }
    const selectedAssets = getSelectedAssetsForBatch();
    if (selectedAssets.length === 0) {
      toast.error('No approved assets are ready for minting in this asset class.');
      return null;
    }
    const std = normalizeTokenStandardKey(assetClass?.tokenStandard);
    if (std === 'ERC20' && !String(formData.erc20MintAmount || '').trim()) {
      toast.error('Enter ERC-20 mint amount per asset (integer string, e.g. wei for 18 decimals).');
      return null;
    }
    if (std === 'ERC1155' && !String(formData.erc1155MintAmount || '').trim()) {
      toast.error('Enter ERC-1155 mint amount per asset.');
      return null;
    }
    return { contractArtifact, selectedAssets };
  };

  const handlePrepareUnsignedDeployment = async () => {
    const v = validateStep5ForOffline();
    if (!v) return;
    if (contractAddressEffective()) {
      toast.error('This class already has a deployed contract address.');
      return;
    }
    const { contractArtifact } = v;
    const bc = String(contractArtifact.bytecode || '').trim();
    if (!bc.startsWith('0x') || bc.length < 4) {
      toast.error(
        'Creation bytecode (0x…) is required to deploy (use a standard template or paste bytecode for custom).'
      );
      return;
    }
    if (preparedDeployment) {
      toast.error('Unsigned deployment already prepared. Clear or broadcast it first.');
      return;
    }
    setIsSubmitting(true);
    try {
      const cName = sanitizeContractName(`${assetClass.name}BatchNFT`);
      const constructorParameters = await buildSharedSeries721ConstructorParameters({
        contractTemplateId,
        isAdmin: Boolean(isAdmin),
        isPlatformStandard: Boolean(assetClass.isPlatformStandard),
        assetClass,
        contractName: cName,
        tokenizerWalletAddresses: [currentIssuer?.walletAddress, formData.signerAddress]
          .map((w) => String(w || '').trim())
          .filter(Boolean)
      });
      const deploymentPayload = await prepareContractDeployment(assetClass.id, {
        assetClassId: assetClass.id,
        contractName: cName,
        signerAddress: formData.signerAddress,
        contractAbi: contractArtifact.abiJson,
        bytecode: contractArtifact.bytecode,
        constructorParameters
      });
      await ensureBatchRecord();
      setPreparedDeployment(deploymentPayload);
      toast.success('Unsigned deployment transaction ready.');
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Prepare deployment failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBroadcastSignedDeployment = async () => {
    const v = validateStep5ForOffline();
    if (!v) return;
    if (!preparedDeployment) {
      toast.error('Prepare an unsigned deployment first.');
      return;
    }
    const signedText = String(signedDeploymentInput || '').trim();
    if (!signedText) {
      toast.error('Paste or upload the signed deployment transaction first.');
      return;
    }
    const { contractArtifact } = v;
    setIsSubmitting(true);
    try {
      const signedDeployment = parseSignedTransactionInput(signedText);
      const declaredTokenStandard = getDeclaredTokenStandardForContractTemplate(contractTemplateId);
      const deploymentResponse = await submitContractDeployment(assetClass.id, {
        assetClassId: assetClass.id,
        contractName: sanitizeContractName(`${assetClass.name}BatchNFT`),
        signedTransactionHex: signedDeployment.signedTransactionHex,
        expectedContractAddress: signedDeployment.expectedContractAddress,
        contractAbi: contractArtifact.abiJson,
        bytecode: null,
        constructorParameters: null,
        ...(declaredTokenStandard ? { declaredTokenStandard } : {})
      });
      setDeploymentStatus(deploymentResponse);
      resetDeploymentOffline();
      const refreshed = await pollForDeploymentConfirmation(assetClass.id);
      const addr = String(refreshed?.contractAddress || '').trim();
      if (addr) {
        setResolvedContractAddress(addr);
        toast.success('Deployment submitted.');
      } else {
        toast.error(
          'Deployment was submitted but the contract address is not available yet. Refresh asset classes and try Prepare unsigned mint again.'
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Submit deployment failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrepareUnsignedMint = async () => {
    const v = validateStep5ForOffline();
    if (!v) return;
    const addr = contractAddressEffective();
    if (!addr) {
      toast.error('Deploy the class contract first using the offline deployment flow above.');
      return;
    }
    if (preparedMintBatch.length > 0) {
      toast.error('Unsigned mints already prepared. Clear or broadcast them first.');
      return;
    }
    const { contractArtifact, selectedAssets } = v;
    const std = normalizeTokenStandardKey(assetClass?.tokenStandard);
    setIsSubmitting(true);
    try {
      const preparedPayloads = [];
      let batchBaseNonceHex = null;

      if (std === 'ERC721') {
        const { tokenIds } = await allocateErc721TokenIds({
          contractAddress: addr,
          contractAbi: contractArtifact.abiJson,
          count: selectedAssets.length
        });
        if (!Array.isArray(tokenIds) || tokenIds.length !== selectedAssets.length) {
          throw new Error('Server did not return the expected number of token IDs.');
        }
        for (let index = 0; index < selectedAssets.length; index += 1) {
          const asset = selectedAssets[index];
          const tokenId = String(tokenIds[index]);
          const mintPrepareBody = {
            signerAddress: formData.signerAddress,
            tokenId,
            ownerWallet: formData.ownerAddress,
            contractAddress: addr,
            contractAbi: contractArtifact.abiJson,
            functionName: contractArtifact.mintFunctionName
          };
          if (index > 0 && batchBaseNonceHex) {
            mintPrepareBody.nonceOverride = incrementNonceHex(batchBaseNonceHex, index);
          }
          const prepared = await prepareMintToken(asset.id, mintPrepareBody);
          if (index === 0) {
            batchBaseNonceHex = prepared.nonce;
          }
          preparedPayloads.push({
            assetId: asset.id,
            assetName: asset.name,
            tokenId,
            mintAmount: null,
            payload: prepared
          });
        }
      } else if (std === 'ERC20') {
        const amt = String(formData.erc20MintAmount || '').trim();
        for (let index = 0; index < selectedAssets.length; index += 1) {
          const asset = selectedAssets[index];
          const mintPrepareBody = {
            signerAddress: formData.signerAddress,
            tokenId: null,
            amount: amt,
            ownerWallet: formData.ownerAddress,
            contractAddress: addr,
            contractAbi: contractArtifact.abiJson,
            functionName: contractArtifact.mintFunctionName,
            tokenStandard: 'ERC20'
          };
          if (index > 0 && batchBaseNonceHex) {
            mintPrepareBody.nonceOverride = incrementNonceHex(batchBaseNonceHex, index);
          }
          const prepared = await prepareMintToken(asset.id, mintPrepareBody);
          if (index === 0) {
            batchBaseNonceHex = prepared.nonce;
          }
          preparedPayloads.push({
            assetId: asset.id,
            assetName: asset.name,
            tokenId: '',
            mintAmount: amt,
            payload: prepared
          });
        }
      } else if (std === 'ERC1155') {
        const { tokenIds } = await allocateErc1155TokenIds({
          contractAddress: addr,
          contractAbi: contractArtifact.abiJson,
          count: selectedAssets.length
        });
        if (!Array.isArray(tokenIds) || tokenIds.length !== selectedAssets.length) {
          throw new Error('Server did not return the expected number of ERC-1155 token type IDs.');
        }
        const amt1155 = String(formData.erc1155MintAmount || '').trim();
        for (let index = 0; index < selectedAssets.length; index += 1) {
          const asset = selectedAssets[index];
          const tokenId = String(tokenIds[index]);
          const mintPrepareBody = {
            signerAddress: formData.signerAddress,
            tokenId,
            amount: amt1155,
            ownerWallet: formData.ownerAddress,
            contractAddress: addr,
            contractAbi: contractArtifact.abiJson,
            functionName: contractArtifact.mintFunctionName,
            tokenStandard: 'ERC1155'
          };
          if (index > 0 && batchBaseNonceHex) {
            mintPrepareBody.nonceOverride = incrementNonceHex(batchBaseNonceHex, index);
          }
          const prepared = await prepareMintToken(asset.id, mintPrepareBody);
          if (index === 0) {
            batchBaseNonceHex = prepared.nonce;
          }
          preparedPayloads.push({
            assetId: asset.id,
            assetName: asset.name,
            tokenId,
            mintAmount: amt1155,
            payload: prepared
          });
        }
      } else {
        throw new Error(`Mint batch is not wired for token standard ${std}. Use ERC-721, ERC-20, or ERC-1155 on the asset class.`);
      }
      await ensureBatchRecord();
      setPreparedMintBatch(preparedPayloads);
      toast.success(
        preparedPayloads.length === 1
          ? 'Unsigned mint transaction ready.'
          : `Prepared ${preparedPayloads.length} unsigned mint transactions. Sign offline, then broadcast.`
      );
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Prepare mint failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBroadcastSignedMints = async () => {
    const v = validateStep5ForOffline();
    if (!v) return;
    if (preparedMintBatch.length === 0) {
      toast.error('Prepare unsigned mint transactions first.');
      return;
    }
    const signedText = String(signedMintBatchInput || '').trim();
    if (!signedText) {
      toast.error('Paste or upload signed mint signer output(s) first.');
      return;
    }
    const addr = contractAddressEffective();
    if (!addr) {
      toast.error('Missing contract address.');
      return;
    }
    const { contractArtifact } = v;
    const std = normalizeTokenStandardKey(assetClass?.tokenStandard);
    setIsSubmitting(true);
    try {
      const batch = await ensureBatchRecord();
      const signedBatch = parseSignedTransactionInput(signedText);
      const signedEntries = Array.isArray(signedBatch) ? signedBatch : [signedBatch];
      if (signedEntries.length !== preparedMintBatch.length) {
        throw new Error(`Expected ${preparedMintBatch.length} signed mint payloads.`);
      }
      const mintResults = [];
      const mintedTokens = [];
      for (let index = 0; index < preparedMintBatch.length; index += 1) {
        const preparedItem = preparedMintBatch[index];
        const signedItem = signedEntries[index];
        const submitBody = {
          signedTransactionHex: signedItem.signedTransactionHex,
          ownerWallet: formData.ownerAddress,
          contractAddress: addr,
          contractAbi: contractArtifact.abiJson,
          functionName: contractArtifact.mintFunctionName
        };
        if (std === 'ERC721') {
          submitBody.tokenId = preparedItem.tokenId;
        } else if (std === 'ERC20') {
          submitBody.tokenId = '0';
          submitBody.amount = preparedItem.mintAmount || formData.erc20MintAmount;
          submitBody.tokenStandard = 'ERC20';
        } else if (std === 'ERC1155') {
          submitBody.tokenId = preparedItem.tokenId;
          submitBody.amount = preparedItem.mintAmount || formData.erc1155MintAmount;
          submitBody.tokenStandard = 'ERC1155';
        } else {
          submitBody.tokenId = preparedItem.tokenId;
        }
        const result = await submitMintToken(preparedItem.assetId, submitBody);
        mintResults.push(result);
        const st = result?.status;
        const mintFailed =
          st === 'Failed' ||
          st === 5 ||
          String(st || '')
            .toLowerCase()
            .includes('fail');
        if (mintFailed) {
          const reason = result?.failureReason || result?.FailureReason || 'On-chain mint failed.';
          const label =
            std === 'ERC20'
              ? `asset ${preparedItem.assetName || preparedItem.assetId}`
              : `token ID ${preparedItem.tokenId || '—'}`;
          toast.error(`Mint failed for ${label}: ${reason}`);
          throw new Error(reason);
        }
        const mintedToken = await pollForMintedAsset(preparedItem.assetId);
        if (mintedToken) {
          mintedTokens.push({
            assetId: preparedItem.assetId,
            tokenId: mintedToken.tokenId,
            transactionHash: mintedToken.mintTransactionHash
          });
        }
      }
      setMintStatus({
        status: 'complete',
        count: mintResults.length,
        assetIds: preparedMintBatch.map((item) => item.assetId),
        requestStatuses: mintResults.map((result) => result.status || 'submitted'),
        mintedTokens
      });
      resetMintOffline();
      toast.success(
        `Batch ${batch.batchNumber}: ${mintResults.length} signed mint(s) broadcast to the Ping chain.`
      );
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Broadcast mints failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <AssetClassInfo>
              <div className="class-name">{assetClass.name}</div>
              <div className="class-details">
                Contract: {assetClass.contractAddress} • Trading Unit: {assetClass.tradingUnit}
              </div>
              <div className="class-schema">
                <div className="schema-title">
                  <FiInfo className="icon" />
                  Required Metadata Fields
                </div>
                <div className="required-fields">
                  {(assetClass.standardSchema?.required || []).map((field) => (
                    <span key={field} className="field-tag">
                      {field}
                    </span>
                  ))}
                </div>
              </div>
            </AssetClassInfo>

            <Form>
              <FormSection>
                <div className="section-title">
                  <FiPackage className="icon" />
                  Batch Information
                </div>
                
                <FormRow columns="1fr 1fr">
                  <FormGroup>
                    <label>Quantity</label>
                    <input
                      type="number"
                      placeholder="Enter quantity"
                      value={formData.quantity}
                      onChange={(e) => updateFormData('quantity', e.target.value)}
                    />
                    {validationErrors.quantity && (
                      <div className="field-error">{validationErrors.quantity}</div>
                    )}
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Unit Price (USD)</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Enter unit price"
                      value={formData.unitPrice}
                      onChange={(e) => updateFormData('unitPrice', e.target.value)}
                    />
                    {validationErrors.unitPrice && (
                      <div className="field-error">{validationErrors.unitPrice}</div>
                    )}
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <label>Batch Description</label>
                  <textarea
                    placeholder="Describe this batch..."
                    value={formData.description}
                    onChange={(e) => updateFormData('description', e.target.value)}
                  />
                  {validationErrors.description && (
                    <div className="field-error">{validationErrors.description}</div>
                  )}
                </FormGroup>

                {!classHasDeployedContract && (
                  <>
                    <FormGroup>
                      <label>Standard contract template</label>
                      <select
                        value={contractTemplateId}
                        onChange={(e) => setContractTemplateId(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.65rem',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.12)',
                          background: 'rgba(0,0,0,0.35)',
                          color: '#fff'
                        }}
                      >
                        {listStandardContractTemplates().map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name} ({t.versionLabel})
                          </option>
                        ))}
                      </select>
                      <div className="field-hint">
                        {listStandardContractTemplates().find((x) => x.id === contractTemplateId)?.summary}
                        {contractTemplateId !== CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID && (
                          <span style={{ color: '#94a3b8', display: 'block', marginTop: '0.35rem' }}>
                            Preloaded templates are optional; choose Custom to use your own Ping deployment.
                          </span>
                        )}
                      </div>
                      {validationErrors.customContract && (
                        <div className="field-error">{validationErrors.customContract}</div>
                      )}
                      {validationErrors.customBytecode && (
                        <div className="field-error">{validationErrors.customBytecode}</div>
                      )}
                      {validationErrors.customMintFn && (
                        <div className="field-error">{validationErrors.customMintFn}</div>
                      )}
                    </FormGroup>

                    {contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID && (
                      <FormGroup>
                        <label htmlFor="batch-mint-custom-abi">Contract ABI (JSON array) *</label>
                        <textarea
                          id="batch-mint-custom-abi"
                          rows={6}
                          placeholder='[{"type":"function","name":"mint",...}]'
                          value={customContractAbiJson}
                          onChange={(e) => setCustomContractAbiJson(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.65rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.12)',
                            background: 'rgba(0,0,0,0.35)',
                            color: '#e2e8f0',
                            fontFamily: 'Consolas, Monaco, monospace',
                            fontSize: '0.8rem'
                          }}
                        />
                        <div className="field-hint">
                          Must match the contract at the class address (mint) or the bytecode you deploy below.
                        </div>
                      </FormGroup>
                    )}

                    {contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID && !assetClass?.contractAddress && (
                      <FormGroup>
                        <label htmlFor="batch-mint-custom-bytecode">Creation bytecode (hex) *</label>
                        <textarea
                          id="batch-mint-custom-bytecode"
                          rows={3}
                          placeholder="0x60806040…"
                          value={customContractBytecode}
                          onChange={(e) => setCustomContractBytecode(e.target.value.trim())}
                          style={{
                            width: '100%',
                            padding: '0.65rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.12)',
                            background: 'rgba(0,0,0,0.35)',
                            color: '#e2e8f0',
                            fontFamily: 'Consolas, Monaco, monospace',
                            fontSize: '0.8rem'
                          }}
                        />
                        <div className="field-hint">Required when this class has no deployed contract yet.</div>
                      </FormGroup>
                    )}

                    {contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID && (
                      <FormGroup>
                        <label htmlFor="batch-mint-custom-mint-fn">Mint function name *</label>
                        <input
                          id="batch-mint-custom-mint-fn"
                          type="text"
                          placeholder="mint"
                          value={customMintFunctionName}
                          onChange={(e) => setCustomMintFunctionName(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.65rem',
                            borderRadius: '8px',
                            border: '1px solid rgba(255,255,255,0.12)',
                            background: 'rgba(0,0,0,0.35)',
                            color: '#fff'
                          }}
                        />
                      </FormGroup>
                    )}
                  </>
                )}
              </FormSection>
            </Form>
          </StepContent>
        );

      case 2:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Form>
              <FormSection>
                <div className="section-title">
                  <FiShield className="icon" />
                  Token Configuration
                </div>
                
                <FormRow columns="1fr 1fr">
                  <FormGroup>
                    <label>Offline Signer Address *</label>
                    <input
                      type="text"
                      placeholder="0xSignerAddress"
                      value={formData.signerAddress}
                      onChange={(e) => updateFormData('signerAddress', e.target.value)}
                    />
                    <div className="field-hint">
                      Must match the signer used by the separate offline signer app. Saved for your account in this
                      browser.
                    </div>
                    {validationErrors.signerAddress && (
                      <div className="field-error">{validationErrors.signerAddress}</div>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <label>Owner Address *</label>
                    <input
                      type="text"
                      placeholder="0x..."
                      value={formData.ownerAddress}
                      onChange={(e) => updateFormData('ownerAddress', e.target.value)}
                    />
                    <div className="field-hint">Wallet address that will own the minted tokens</div>
                    {validationErrors.ownerAddress && (
                      <div className="field-error">{validationErrors.ownerAddress}</div>
                    )}
                  </FormGroup>

                  {normalizeTokenStandardKey(assetClass?.tokenStandard) === 'ERC20' && (
                    <FormGroup>
                      <label>ERC-20 mint amount per asset *</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="1000000000000000000"
                        value={formData.erc20MintAmount}
                        onChange={(e) => updateFormData('erc20MintAmount', e.target.value)}
                      />
                      <div className="field-hint">
                        Integer string in smallest units (wei-style). Default is 1e18 — one full token if the contract
                        uses 18 decimals.
                      </div>
                    </FormGroup>
                  )}

                  {normalizeTokenStandardKey(assetClass?.tokenStandard) === 'ERC1155' && (
                    <FormGroup>
                      <label>ERC-1155 amount per asset *</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="1"
                        value={formData.erc1155MintAmount}
                        onChange={(e) => updateFormData('erc1155MintAmount', e.target.value)}
                      />
                      <div className="field-hint">Units to mint for each asset&apos;s token type ID (often 1 for unique lots).</div>
                    </FormGroup>
                  )}
                  
                  <FormGroup>
                    <label>Custodian Address *</label>
                    <input
                      type="text"
                      placeholder="0x..."
                      value={formData.custodianAddress}
                      onChange={(e) => updateFormData('custodianAddress', e.target.value)}
                    />
                    <div className="field-hint">Address of the authorized custodian</div>
                    {validationErrors.custodianAddress && (
                      <div className="field-error">{validationErrors.custodianAddress}</div>
                    )}
                  </FormGroup>
                </FormRow>
                
                <FormRow columns="1fr 1fr">
                  <FormGroup>
                    <label>Token Symbol Prefix *</label>
                    <input
                      type="text"
                      placeholder="GOLD-"
                      value={formData.tokenSymbolPrefix}
                      onChange={(e) => updateFormData('tokenSymbolPrefix', e.target.value)}
                    />
                    <div className="field-hint">Prefix for token symbols (e.g., GOLD-001, GOLD-002)</div>
                    {validationErrors.tokenSymbolPrefix && (
                      <div className="field-error">{validationErrors.tokenSymbolPrefix}</div>
                    )}
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Optional — legacy serial (display only)</label>
                    <input
                      type="number"
                      placeholder="Not used for chain token ID"
                      value={formData.startingSerialNumber}
                      onChange={(e) => updateFormData('startingSerialNumber', e.target.value)}
                    />
                    <div className="field-hint">
                      ERC-721 token IDs are chosen on-chain when you prepare mints. This field is only for your own
                      records if you still use it elsewhere; offline mint uses automatic IDs.
                    </div>
                    {validationErrors.startingSerialNumber && (
                      <div className="field-error">{validationErrors.startingSerialNumber}</div>
                    )}
                  </FormGroup>
                </FormRow>
                
                <FormRow columns="1fr 1fr">
                  <FormGroup>
                    <label>Minting Location *</label>
                    <input
                      type="text"
                      placeholder="Warehouse ID or Location"
                      value={formData.mintingLocation}
                      onChange={(e) => updateFormData('mintingLocation', e.target.value)}
                    />
                    <div className="field-hint">Physical location where assets are stored</div>
                    {validationErrors.mintingLocation && (
                      <div className="field-error">{validationErrors.mintingLocation}</div>
                    )}
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Quality Certification ID</label>
                    <input
                      type="text"
                      placeholder="Certification ID"
                      value={formData.qualityCertificationId}
                      onChange={(e) => updateFormData('qualityCertificationId', e.target.value)}
                    />
                    <div className="field-hint">Third-party quality verification ID</div>
                  </FormGroup>
                </FormRow>
                
                <FormRow columns="1fr 1fr">
                  <FormGroup>
                    <label>Insurance Policy ID</label>
                    <input
                      type="text"
                      placeholder="Policy Number"
                      value={formData.insurancePolicyId}
                      onChange={(e) => updateFormData('insurancePolicyId', e.target.value)}
                    />
                    <div className="field-hint">Insurance coverage policy number</div>
                  </FormGroup>
                  
                  <FormGroup>
                    <label>Minting Reason</label>
                    <select
                      value={formData.mintingReason}
                      onChange={(e) => updateFormData('mintingReason', e.target.value)}
                    >
                      <option value="NEW_INVENTORY">New Inventory</option>
                      <option value="REPLACEMENT">Replacement</option>
                      <option value="REBALANCING">Portfolio Rebalancing</option>
                      <option value="TRANSFER">Location Transfer</option>
                      <option value="QUALITY_UPGRADE">Quality Upgrade</option>
                    </select>
                    <div className="field-hint">Reason for minting these tokens</div>
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <label>Storage Conditions</label>
                  <textarea
                    placeholder="Describe storage conditions, temperature, security measures..."
                    value={formData.storageConditions}
                    onChange={(e) => updateFormData('storageConditions', e.target.value)}
                    rows={3}
                  />
                  <div className="field-hint">Describe how the physical assets are stored</div>
                </FormGroup>
                
                <FormGroup>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      id="autoSerialization"
                      checked={formData.enableAutoSerialization}
                      onChange={(e) => updateFormData('enableAutoSerialization', e.target.checked)}
                    />
                    <label htmlFor="autoSerialization">Enable Auto-Serialization</label>
                  </div>
                  <div className="field-hint">
                    Automatically assign sequential serial numbers to tokens
                  </div>
                </FormGroup>
              </FormSection>
            </Form>
          </StepContent>
        );

      case 3:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Form>
              <FormSection>
                <div className="section-title">
                  <FiShield className="icon" />
                  Asset Metadata
                </div>
                
                {isRegistryTokenizeFlow && (
                  <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: '1rem', lineHeight: 1.5 }}>
                    Metadata was set when this asset was created. Fields below are read-only.
                  </p>
                )}
                <FormRow columns="1fr 1fr">
                  {(assetClass.standardSchema?.required || []).map((field) => {
                    const spec = assetClass.standardSchema?.specifications?.[field];
                    return (
                      <FormGroup key={field}>
                        <label>{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                        {spec?.enum ? (
                          <select
                            value={formData.metadata[field] || ''}
                            onChange={(e) => updateMetadata(field, e.target.value)}
                            disabled={isRegistryTokenizeFlow}
                          >
                            <option value="">Select {field}</option>
                            {spec.enum.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={spec?.type === 'number' ? 'number' : 'text'}
                            placeholder={`Enter ${field}`}
                            value={formData.metadata[field] || ''}
                            onChange={(e) => updateMetadata(field, e.target.value)}
                            min={spec?.min}
                            max={spec?.max}
                            step={spec?.step}
                            readOnly={isRegistryTokenizeFlow}
                            disabled={isRegistryTokenizeFlow}
                          />
                        )}
                        {spec?.unit && <div className="field-hint">Unit: {spec.unit}</div>}
                      </FormGroup>
                    );
                  })}
                </FormRow>
                
                {validationErrors.metadata && (
                  <div className="field-error">{validationErrors.metadata}</div>
                )}
              </FormSection>
            </Form>
          </StepContent>
        );

      case 4:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <FormSection>
              <div className="section-title">
                <FiUpload className="icon" />
                Supporting Documents
              </div>

              {isRegistryTokenizeFlow && (
                <p style={{ color: '#94a3b8', fontSize: '0.88rem', marginBottom: '1rem', lineHeight: 1.5 }}>
                  Documents were attached when this asset was created. This list is read-only.
                </p>
              )}

              {!isRegistryTokenizeFlow && (
                <>
                  <DocumentUpload onClick={() => document.getElementById('batch-file-input').click()}>
                    <FiUpload className="upload-icon" />
                    <div className="upload-text">Upload Documents</div>
                    <div className="upload-hint">Click to browse or drag and drop files here</div>
                  </DocumentUpload>

                  <input
                    id="batch-file-input"
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.png,.doc,.docx"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileUpload(e.target.files)}
                  />
                </>
              )}

              {formData.documents.length > 0 && (
                <UploadedFiles>
                  {formData.documents.map((file) => {
                    const rowKey = assetDocumentRowKey(file);
                    const hasLocal = file?.nativeFile instanceof File;
                    const hasCid = Boolean(String(file?.ipfsCid ?? file?.IpfsCid ?? '').trim());
                    const canOpen = hasLocal || hasCid;
                    const sizeMb = file.size > 0 ? (file.size / 1024 / 1024).toFixed(2) : null;
                    return (
                      <div key={file.id} className="file-item">
                        <FiFile className="file-icon" />
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">
                          {sizeMb != null ? `${sizeMb} MB` : '—'}
                        </span>
                        <MiniButton
                          className="file-open"
                          disabled={!canOpen || openingDocumentKey === rowKey}
                          title={
                            !canOpen
                              ? 'Document is not available to open from here'
                              : 'Open or download document'
                          }
                          onClick={() => openWizardDocument(file)}
                        >
                          <FiEye aria-hidden />
                          {openingDocumentKey === rowKey ? 'Opening…' : 'View'}
                        </MiniButton>
                      </div>
                    );
                  })}
                </UploadedFiles>
              )}
              
              {validationErrors.documents && (
                <div className="field-error">{validationErrors.documents}</div>
              )}

              {isRegistryTokenizeFlow && formData.documents.length === 0 && (
                <p style={{ color: '#94a3b8', fontSize: '0.88rem' }}>No documents on file for this asset.</p>
              )}
            </FormSection>
          </StepContent>
        );

      case 5:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <ReviewSection>
              <div className="review-title">
                <FiCheck className="icon" />
                {isRegistryTokenizeFlow ? 'Review & mint' : 'Review Batch Details'}
              </div>

              {isRegistryTokenizeFlow && preselectedAsset && (
                <div className="review-item">
                  <span className="item-label">Asset</span>
                  <span className="item-value">
                    {preselectedAsset.name || preselectedAsset.title || preselectedAsset.id}
                  </span>
                </div>
              )}

              <div className="review-item">
                <span className="item-label">Asset Class</span>
                <span className="item-value">{assetClass.name}</span>
              </div>

              <div className="review-item">
                <span className="item-label">Quantity</span>
                <span className="item-value">
                  {formData.quantity} {assetClass.tradingUnit}
                </span>
              </div>

              <div className="review-item">
                <span className="item-label">
                  {isRegistryTokenizeFlow ? 'Assets to mint' : 'Eligible Backend Assets'}
                </span>
                <span className="item-value">
                  {isRegistryTokenizeFlow ? getSelectedAssetsForBatch().length : eligibleAssets.length}
                </span>
              </div>
              
              <div className="review-item">
                <span className="item-label">Unit Price</span>
                <span className="item-value">${formData.unitPrice}</span>
              </div>
              
              <div className="review-item">
                <span className="item-label">Total Value</span>
                <span className="item-value">
                  ${(parseFloat(formData.unitPrice) * parseInt(formData.quantity)).toFixed(2)}
                </span>
              </div>
              
              <div className="review-item">
                <span className="item-label">Documents</span>
                <span className="item-value">{formData.documents.length} files</span>
              </div>
              
              <div className="review-item">
                <span className="item-label">Owner Address</span>
                <span className="item-value">{formData.ownerAddress}</span>
              </div>

              <div className="review-item">
                <span className="item-label">Signer Address</span>
                <span className="item-value">{formData.signerAddress}</span>
              </div>
              
              <div className="review-item">
                <span className="item-label">Custodian Address</span>
                <span className="item-value">{formData.custodianAddress}</span>
              </div>
              
              <div className="review-item">
                <span className="item-label">On-chain token IDs</span>
                <span className="item-value">
                  Assigned automatically when you prepare unsigned mints (consecutive free IDs on the contract).
                  {formData.tokenSymbolPrefix ? ` Label prefix: ${formData.tokenSymbolPrefix}` : ''}
                </span>
              </div>
              
              <div className="review-item">
                <span className="item-label">Minting Location</span>
                <span className="item-value">{formData.mintingLocation}</span>
              </div>
              
              {formData.qualityCertificationId && (
                <div className="review-item">
                  <span className="item-label">Quality Certification</span>
                  <span className="item-value">{formData.qualityCertificationId}</span>
                </div>
              )}
              
              {formData.insurancePolicyId && (
                <div className="review-item">
                  <span className="item-label">Insurance Policy</span>
                  <span className="item-value">{formData.insurancePolicyId}</span>
                </div>
              )}
              
              <div className="review-item">
                <span className="item-label">Minting Reason</span>
                <span className="item-value">{formData.mintingReason}</span>
              </div>
              
              {Object.entries(formData.metadata).map(([key, value]) => (
                <div key={key} className="review-item">
                  <span className="item-label">{key}</span>
                  <span className="item-value">{value}</span>
                </div>
              ))}

              {!contractAddressEffective() && (
                <>
                  <StatusPanel>
                    <div className="title">Offline signing &amp; broadcast (Ping chain)</div>
                    <FlowSteps>
                      <li>
                        <strong>Prepare unsigned deployment</strong> — The API builds the unsigned contract-creation
                        transaction JSON your offline signer needs. Private keys never leave the signer.
                      </li>
                      <li>
                        <strong>Sign</strong> — Copy or download the JSON, then sign with the offline signer address you
                        entered (cold wallet or Ping offline signer app).
                      </li>
                      <li>
                        <strong>Upload or paste signed data</strong> — Load the signer output file or paste JSON / raw{' '}
                        <code>0x</code> <code>signedTransactionHex</code>. Then <strong>Broadcast signed deployment</strong>{' '}
                        so the backend submits <code>xcb_sendRawTransaction</code>.
                      </li>
                    </FlowSteps>
                    <div className="meta">
                      <span>Asset class: {assetClass.code || assetClass.name}</span>
                      <span>
                        Target mints:{' '}
                        {Math.min(parseInt(formData.quantity || 0, 10), targetMintPoolSize)}
                      </span>
                    </div>
                  </StatusPanel>
                  {preparedDeployment && (
                    <>
                      <PayloadBox>
                        <div className="title">Unsigned contract deployment transaction</div>
                        <div className="hint">
                          Unsigned transaction for deploying the class NFT contract. Sign offline only — do not sign in the
                          browser.
                        </div>
                        <PayloadActions>
                          <MiniButton type="button" onClick={() => handleCopyPayload(preparedDeployment)}>
                            <FiCopy />
                            Copy JSON
                          </MiniButton>
                          <MiniButton
                            type="button"
                            onClick={() =>
                              handleDownloadPayload(
                                `${assetClass?.code || 'asset-class'}-batch-deploy-prepared.json`,
                                preparedDeployment
                              )
                            }
                          >
                            <FiDownload />
                            Download JSON
                          </MiniButton>
                        </PayloadActions>
                        <textarea readOnly value={formatPreparedPayload(preparedDeployment)} />
                      </PayloadBox>
                      <PayloadBox>
                        <div className="title">Signer output template</div>
                        <div className="hint">Your signer should produce JSON containing signedTransactionHex.</div>
                        <PayloadActions>
                          <MiniButton type="button" onClick={() => handleCopyTemplate(singleSignerTemplate)}>
                            <FiCopy />
                            Copy template
                          </MiniButton>
                        </PayloadActions>
                        <textarea readOnly value={singleSignerTemplate} />
                      </PayloadBox>
                      <PayloadBox>
                        <div className="title">Signed deployment — upload or paste</div>
                        <div className="hint">
                          Upload a JSON file from your signer or paste JSON / raw <code>signedTransactionHex</code>.
                        </div>
                        <PayloadActions>
                          <FilePickLabel>
                            <FiUpload aria-hidden />
                            Upload signed file
                            <input
                              type="file"
                              accept=".json,.txt,text/json,application/json"
                              onChange={handleSignedDeploymentFileSelected}
                            />
                          </FilePickLabel>
                        </PayloadActions>
                        <textarea
                          value={signedDeploymentInput}
                          onChange={(e) => setSignedDeploymentInput(e.target.value)}
                          placeholder="Paste signedTransactionHex or full signer JSON here…"
                        />
                      </PayloadBox>
                    </>
                  )}
                  <OfflineFlowActionRow>
                    <OfflineFlowSecondaryButton
                      type="button"
                      onClick={resetDeploymentOffline}
                      disabled={
                        isSubmitting || (!preparedDeployment && !String(signedDeploymentInput || '').trim())
                      }
                    >
                      <FiRefreshCw />
                      Clear prepared / signed
                    </OfflineFlowSecondaryButton>
                    <OfflineFlowPrimaryButton
                      type="button"
                      onClick={handlePrepareUnsignedDeployment}
                      disabled={isSubmitting || Boolean(preparedDeployment)}
                    >
                      <FiCpu />
                      Prepare unsigned deployment
                    </OfflineFlowPrimaryButton>
                    <OfflineFlowPrimaryButton
                      type="button"
                      onClick={handleBroadcastSignedDeployment}
                      disabled={
                        isSubmitting ||
                        !preparedDeployment ||
                        !String(signedDeploymentInput || '').trim()
                      }
                    >
                      <FiCpu />
                      Broadcast signed deployment
                    </OfflineFlowPrimaryButton>
                  </OfflineFlowActionRow>
                </>
              )}

              {contractAddressEffective() && mintStatus?.status !== 'complete' && (
                <>
                  <StatusPanel>
                    <div className="title">Offline signing &amp; broadcast — mint (Ping chain)</div>
                    <FlowSteps>
                      <li>
                        <strong>Prepare unsigned mint</strong> — The API builds the unsigned mint JSON your offline signer
                        needs (one object if there is a single mint, otherwise a JSON array in mint order). Private keys
                        never leave the signer.
                      </li>
                      <li>
                        <strong>Sign</strong> — Copy or download the JSON, then sign with the offline signer address you
                        entered (cold wallet or Ping offline signer app).
                      </li>
                      <li>
                        <strong>Upload or paste signed data</strong> — Paste a <strong>JSON array</strong> of signer outputs
                        in the <strong>same order</strong> as the unsigned payloads (wrap a single result in{' '}
                        <code>[ ]</code> if you only minted one). Then <strong>Broadcast signed mints</strong> so the backend
                        submits each <code>xcb_sendRawTransaction</code>.
                      </li>
                    </FlowSteps>
                    <div className="meta">
                      <span>Asset class: {assetClass.code || assetClass.name}</span>
                      <span>Contract: {contractAddressEffective()}</span>
                      <span>
                        Target mints:{' '}
                        {Math.min(parseInt(formData.quantity || 0, 10), targetMintPoolSize)}
                      </span>
                    </div>
                  </StatusPanel>
                  {preparedMintBatch.length > 0 && (
                    <div ref={mintPayloadAnchorRef}>
                      <PayloadBox>
                        <div className="title">
                          {preparedMintBatch.length === 1
                            ? 'Unsigned token minting transaction'
                            : 'Unsigned token minting transactions (JSON array)'}
                        </div>
                        <div className="hint">
                          {preparedMintBatch.length === 1
                            ? 'Unsigned transaction for minting on the class NFT contract. Sign offline only — do not sign in the browser.'
                            : 'Unsigned mint payloads in order (one per asset). Sign offline only — do not sign in the browser.'}
                        </div>
                        <PayloadActions>
                          <MiniButton type="button" onClick={() => handleCopyPayload(mintPreparedUnifiedPayload)}>
                            <FiCopy />
                            Copy JSON
                          </MiniButton>
                          <MiniButton
                            type="button"
                            onClick={() =>
                              handleDownloadPayload(
                                `${assetClass?.code || 'asset-class'}-batch-mint-prepared.json`,
                                mintPreparedUnifiedPayload
                              )
                            }
                          >
                            <FiDownload />
                            Download JSON
                          </MiniButton>
                        </PayloadActions>
                        <textarea readOnly value={formatPreparedPayload(mintPreparedUnifiedPayload)} />
                      </PayloadBox>
                      <PayloadBox>
                        <div className="title">Signer output template</div>
                        <div className="hint">Your signer should produce JSON containing signedTransactionHex.</div>
                        <PayloadActions>
                          <MiniButton
                            type="button"
                            onClick={() => handleCopyTemplate(mintSignerTemplateForDisplay)}
                          >
                            <FiCopy />
                            Copy template
                          </MiniButton>
                        </PayloadActions>
                        <textarea readOnly value={mintSignerTemplateForDisplay} />
                      </PayloadBox>
                      <PayloadBox>
                        <div className="title">Signed batch mints — upload or paste</div>
                        <div className="hint">
                          Upload a JSON file from your signer or paste a JSON array of signer outputs. Order must match the
                          unsigned mint list. Then choose <strong>Broadcast signed mints</strong> so the backend submits each{' '}
                          <code>xcb_sendRawTransaction</code>.
                        </div>
                        <PayloadActions>
                          <FilePickLabel>
                            <FiUpload aria-hidden />
                            Upload signed file
                            <input
                              type="file"
                              accept=".json,.txt,text/json,application/json"
                              onChange={handleSignedBatchMintFileSelected}
                            />
                          </FilePickLabel>
                        </PayloadActions>
                        <textarea
                          value={signedMintBatchInput}
                          onChange={(e) => setSignedMintBatchInput(e.target.value)}
                          placeholder='Paste JSON array, e.g. [{ "signedTransactionHex": "0x..." }, ...]'
                        />
                      </PayloadBox>
                    </div>
                  )}
                  <OfflineFlowActionRow>
                    <OfflineFlowSecondaryButton
                      type="button"
                      onClick={resetMintOffline}
                      disabled={
                        isSubmitting ||
                        (preparedMintBatch.length === 0 && !String(signedMintBatchInput || '').trim())
                      }
                    >
                      <FiRefreshCw />
                      Clear prepared / signed
                    </OfflineFlowSecondaryButton>
                    <OfflineFlowPrimaryButton
                      type="button"
                      onClick={handlePrepareUnsignedMint}
                      disabled={isSubmitting || preparedMintBatch.length > 0}
                    >
                      <FiCpu />
                      Prepare unsigned mint
                    </OfflineFlowPrimaryButton>
                    <OfflineFlowPrimaryButton
                      type="button"
                      onClick={handleBroadcastSignedMints}
                      disabled={
                        isSubmitting ||
                        preparedMintBatch.length === 0 ||
                        !String(signedMintBatchInput || '').trim()
                      }
                    >
                      <FiCpu />
                      Broadcast signed mints
                    </OfflineFlowPrimaryButton>
                  </OfflineFlowActionRow>
                </>
              )}

              {createdBatch &&
                !preparedDeployment &&
                preparedMintBatch.length === 0 && (
                  <StatusPanel>
                    <div className="title">Batch Created</div>
                    <div className="message">
                      Batch {createdBatch.batchNumber} is recorded locally in the current frontend session.
                    </div>
                    <div className="meta">
                      <span>Batch ID: {createdBatch.id}</span>
                      <span>Status: {createdBatch.status}</span>
                    </div>
                  </StatusPanel>
                )}

              {deploymentStatus && (
                <StatusPanel>
                  <div className="title">Deployment Submitted</div>
                  <div className="message">
                    Contract deployment was broadcast; the UI is polling the Ping chain for confirmation.
                  </div>
                  <div className="meta">
                    <span>
                      Transaction:{' '}
                      <HashButton onClick={() => setInspectedTransactionHash(deploymentStatus.deploymentTransactionHash)}>
                        {deploymentStatus.deploymentTransactionHash}
                      </HashButton>
                    </span>
                    <span>
                      Status:{' '}
                      <StatusBadge status={deploymentStatus.status || 'submitted'}>
                        {deploymentStatus.status || 'submitted'}
                      </StatusBadge>
                    </span>
                  </div>
                </StatusPanel>
              )}

              {mintStatus && (
                <StatusPanel>
                  <div className="title">Mint Requests Submitted</div>
                  <div className="message">
                    Signed mint transactions were broadcast; each asset is being reconciled on the Ping chain.
                  </div>
                  <div className="meta">
                    <span>Mint count: {mintStatus.count}</span>
                    <span>
                      Requests:{' '}
                      <StatusBadge status={mintStatus.requestStatuses?.some((status) => status?.toLowerCase() === 'failed') ? 'failed' : 'submitted'}>
                        {mintStatus.requestStatuses?.join(', ') || 'submitted'}
                      </StatusBadge>
                    </span>
                    <span>
                      Confirmed txs:{' '}
                      <StatusBadge status={mintStatus.mintedTokens?.length ? 'minted' : 'submitted'}>
                        {mintStatus.mintedTokens?.length || 0}
                      </StatusBadge>
                    </span>
                  </div>
                  {mintStatus.mintedTokens?.length > 0 && (
                    <div className="meta">
                      {mintStatus.mintedTokens.map((item) => (
                        <span key={item.assetId}>
                          {item.tokenId}:{' '}
                          <HashButton onClick={() => setInspectedTransactionHash(item.transactionHash)}>
                            {item.transactionHash}
                          </HashButton>
                        </span>
                      ))}
                    </div>
                  )}
                </StatusPanel>
              )}
            </ReviewSection>
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <WizardContainer>
      <TransactionLookupDrawer
        transactionHash={inspectedTransactionHash}
        onClose={() => setInspectedTransactionHash(null)}
      />
      {(loading || isSubmitting) && (
        <LoadingOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="loading-spinner" />
          <div className="loading-text">
            {isSubmitting ? 'Preparing or submitting offline-signing steps...' : 'Processing...'}
          </div>
        </LoadingOverlay>
      )}
      
      <WizardHeader>
        <WizardTitle>
          <FiPackage className="icon" />
          {isRegistryTokenizeFlow ? (
            <>
              Tokenize{' '}
              {preselectedAsset?.name ||
                preselectedAsset?.title ||
                (preselectedAsset?.id ? String(preselectedAsset.id).slice(0, 12) : 'asset')}
              {assetClass?.name ? ` · ${assetClass.name}` : ''}
            </>
          ) : (
            <>Add New Batch to {assetClass?.name}</>
          )}
        </WizardTitle>
        <CloseButton onClick={onClose}>
          <FiX />
        </CloseButton>
      </WizardHeader>

      <ProgressBar>
        <ProgressSteps>
          {stepsForProgressBar.map((step, barIndex) => (
            <ProgressStep
              key={step.id}
              active={currentStep === step.id}
              completed={currentStep > step.id}
            >
              <div className="step-circle">
                {currentStep > step.id ? (
                  <FiCheck />
                ) : isRegistryTokenizeFlow ? (
                  barIndex + 1
                ) : (
                  step.id
                )}
              </div>
              <div className="step-label">{step.label}</div>
            </ProgressStep>
          ))}
        </ProgressSteps>
      </ProgressBar>

      <AnimatePresence mode="wait">
        {renderStepContent()}
      </AnimatePresence>

      <WizardActions>
        <ActionButton
          variant="secondary"
          onClick={prevStep}
          disabled={currentStep <= minStep}
        >
          <FiArrowLeft />
          Previous
        </ActionButton>
        
        {currentStep < steps.length ? (
          <ActionButton
            variant="primary"
            onClick={nextStep}
          >
            Next
            <FiArrowRight />
          </ActionButton>
        ) : (
          <div
            style={{
              marginLeft: 'auto',
              maxWidth: '340px',
              textAlign: 'right',
              color: '#94a3b8',
              fontSize: '0.88rem',
              lineHeight: 1.45
            }}
          >
            {mintStatus?.status === 'complete' ? (
              <>
                <FiCheck style={{ verticalAlign: '-2px', marginRight: '0.35rem', color: '#00ff88' }} />
                {isRegistryTokenizeFlow
                  ? 'Tokenization complete. You can close this dialog.'
                  : 'Batch tokenization complete. You can close this dialog.'}
              </>
            ) : (
              <>
                On this step, use <strong style={{ color: '#e2e8f0' }}>Prepare</strong> and{' '}
                <strong style={{ color: '#e2e8f0' }}>Broadcast</strong> in the offline signing sections above — same flow as
                the deploy-contract modal.
              </>
            )}
          </div>
        )}
      </WizardActions>
    </WizardContainer>
  );
}

export default BatchMintingWizard; 